import { ArrowUpIcon } from 'lucide-react'
import { type KeyboardEvent, type ReactNode, useState } from 'react'

import { cn } from '@/lib/ui/style-helpers'

import { Button, type ButtonProps } from '@/components/lemmala-design-system/button/button'
import { Textarea } from '@/components/lemmala-design-system/textarea/textarea'

import { DRAWER_LAYOUT_LABELS } from './labels'

export type DrawerCommentInputProps = {
  placeholder?: string
  onSubmit: (body: string) => void | Promise<void>
  disabled?: boolean
  submitSize?: ButtonProps['size']
  className?: string
  // Shown inside the box above the typing area, e.g. a tag scoping the message.
  topContent?: ReactNode
}

export const DrawerCommentInput = ({
  placeholder = DRAWER_LAYOUT_LABELS.commentPlaceholder,
  onSubmit,
  disabled = false,
  submitSize = 'icon-sm',
  className,
  topContent,
}: DrawerCommentInputProps) => {
  const [comment, setComment] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    const trimmed = comment.trim()
    if (!trimmed || disabled || isSubmitting) return

    setIsSubmitting(true)
    try {
      await onSubmit(trimmed)
      setComment('')
    } catch {
      // Keep the draft when submit fails so the user can retry.
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      void handleSubmit()
    }
  }

  const isInputDisabled = disabled || isSubmitting
  const isSubmitDisabled = !comment.trim() || isInputDisabled

  return (
    <div className={cn('relative mt-6', className)}>
      <Textarea
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        placeholder={placeholder}
        resizable={false}
        className={cn('pb-11 pr-11', topContent ? 'h-28 min-h-28 pt-10' : 'h-20 min-h-20')}
        disabled={isInputDisabled}
        onKeyDown={handleKeyDown}
      />
      {topContent && (
        <div className='absolute left-2 right-12 top-2 flex min-w-0'>{topContent}</div>
      )}
      <Button
        type='button'
        size={submitSize}
        className='absolute bottom-2 right-2'
        disabled={isSubmitDisabled}
        aria-label={DRAWER_LAYOUT_LABELS.addCommentAriaLabel}
        onClick={() => void handleSubmit()}
      >
        <ArrowUpIcon className='size-4' />
      </Button>
    </div>
  )
}
